import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import * as api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../ui/command";
import {
  PRIMARY, ACTION_META, PRIORITY_META, OPEN_STATUSES, StatusLabel, ActionBadge, MemberAvatar,
} from "./shared";

// Suggest a task from the selected query rows: action from page fit / opportunity,
// target = the page most of them rank with, title = action + top query.
function suggestFromRows(rows) {
  if (!rows.length) return { title: "", action_type: "optimize", target_url: "" };
  const fits = rows.map(r => r.page_fit);
  const action_type = fits.includes("cannibalization") ? "fix_cannibalization"
    : rows.every(r => !r.url || r.page_fit === "missing_page" || r.opportunity === "missing_coverage") ? "write_new"
    : "optimize";
  const urlCounts = {};
  rows.forEach(r => { if (r.url) urlCounts[r.url] = (urlCounts[r.url] || 0) + 1; });
  const target_url = Object.entries(urlCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
  const top = [...rows].sort((a, b) => (b.impressions || 0) - (a.impressions || 0))[0].query;
  const more = rows.length > 1 ? ` (+${rows.length - 1})` : "";
  return { title: `${ACTION_META[action_type].label}: ${top}${more}`, action_type, target_url };
}

const field = "w-full px-2.5 py-1.5 text-sm rounded-lg border border-zinc-200 bg-white focus:outline-none focus:ring-2";
const label = "block mb-1 text-[10px] tracking-[0.1em] uppercase font-bold text-zinc-400";

/**
 * Create a task, or (when `rows` are given) add those queries to an existing open task.
 * `rows` = query rows from Search Performance ({query, url, page_fit, opportunity, impressions}).
 */
export default function TaskPicker({ open, onOpenChange, rows = [], members = [], onDone, canAddToExisting = true }) {
  const [mode, setMode] = useState("new");
  const [form, setForm] = useState({});
  const [tasks, setTasks] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("new");
    setError("");
    setForm({ priority: "medium", assignee_user_id: "", due_date: "", description: "", ...suggestFromRows(rows) });
    if (rows.length && canAddToExisting) {
      api.getTasks().then(d => setTasks((d.tasks || []).filter(t => OPEN_STATUSES.includes(t.status)))).catch(() => setTasks([]));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const queries = rows.map(r => r.query);

  const finish = (task, message) => { onDone?.(task, message); onOpenChange(false); };
  const fail = (e) => setError(e?.response?.data?.detail || e?.message || "Something went wrong.");

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const task = await api.createTask({
        title: form.title.trim(),
        description: form.description,
        action_type: form.action_type,
        priority: form.priority,
        target_url: form.target_url || null,
        assignee_user_id: form.assignee_user_id || null,
        due_date: form.due_date || null,
        queries,
      });
      finish(task, `Created ${task.key}`);
    } catch (err) { fail(err); } finally { setSaving(false); }
  };

  const handleAdd = async (task) => {
    setSaving(true);
    setError("");
    try {
      const r = await api.addTaskQueries(task.key, queries);
      finish(task, `Added ${r.added} ${r.added === 1 ? "query" : "queries"} to ${task.key}`);
    } catch (err) { fail(err); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{rows.length ? `${rows.length} ${rows.length === 1 ? "query" : "queries"} → task` : "New task"}</DialogTitle>
          {rows.length > 0 && (
            <DialogDescription>
              {canAddToExisting ? "Create a task for these queries, or add them to an open one." : "Create a task for these queries."}
            </DialogDescription>
          )}
        </DialogHeader>

        {rows.length > 0 && canAddToExisting && (
          <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5 w-fit">
            {[["new", "Create new"], ["existing", "Add to existing"]].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setMode(k)}
                className="px-3 py-1 text-xs font-bold rounded-md transition-colors"
                style={mode === k
                  ? { backgroundColor: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                  : { color: "#71717a" }}
              >
                {l}
              </button>
            ))}
          </div>
        )}

        {mode === "new" ? (
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <span className={label}>Title</span>
              <input required value={form.title || ""} onChange={set("title")} className={field} style={{ "--tw-ring-color": PRIMARY }} autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <span className={label}>Action</span>
                <select value={form.action_type} onChange={set("action_type")} className={field}>
                  {Object.entries(ACTION_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
              </label>
              <label>
                <span className={label}>Priority</span>
                <select value={form.priority} onChange={set("priority")} className={field}>
                  {Object.entries(PRIORITY_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
              </label>
              <label>
                <span className={label}>Assignee</span>
                <select value={form.assignee_user_id} onChange={set("assignee_user_id")} className={field}>
                  <option value="">Unassigned</option>
                  {members.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                </select>
              </label>
              <label>
                <span className={label}>Due</span>
                <input type="date" value={form.due_date} onChange={set("due_date")} className={field} />
              </label>
            </div>
            <label className="block">
              <span className={label}>Target page</span>
              <input value={form.target_url || ""} onChange={set("target_url")} placeholder="https://… (optional)" className={field} />
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => onOpenChange(false)} className="px-3 py-1.5 text-xs font-bold rounded-lg text-zinc-500 hover:bg-zinc-100">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !form.title?.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: PRIMARY }}
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                Create task
              </button>
            </div>
          </form>
        ) : (
          <div>
            <Command className="border border-zinc-200 rounded-lg">
              <CommandInput placeholder="Search open tasks…" />
              <CommandList className="max-h-72">
                <CommandEmpty>{tasks === null ? "Loading…" : "No open tasks."}</CommandEmpty>
                <CommandGroup>
                  {(tasks || []).map(t => (
                    <CommandItem
                      key={t.id}
                      value={`${t.key} ${t.title}`}
                      disabled={saving}
                      onSelect={() => handleAdd(t)}
                      className="gap-2"
                    >
                      <span className="text-[11px] font-bold text-zinc-400 w-14 flex-shrink-0">{t.key}</span>
                      <span className="flex-1 truncate">{t.title}</span>
                      <ActionBadge action={t.action_type} />
                      <StatusLabel status={t.status} />
                      <MemberAvatar member={members.find(m => m.user_id === t.assignee_user_id)} size={18} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
